import React, { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, LinearProgress, Paper, Typography, CircularProgress, IconButton } from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { formatDate } from '@/utils/date-utils';
import type { Biomarker, Client } from '@/types/client';
import { HealthGoalsIcon, NextAppointmentIcon, QuestionnaireIcon, QuestionnaireProgressIcon } from './ClientDetailsV2Icons';
import type { GoalItem } from './clientDetailsV2.utils';
import { getBiomarkerColor, panelTitleSx, toTitleCase } from './clientDetailsV2.utils';
import { getClientOverview, type ClientOverviewResponse, type ClientOverviewAppointment } from '@/lib/api/clients';
import { canJoinAppointment, shouldHideAppointmentJoinRescheduleCancel } from '@/utils/appointments';
import { Tag } from '@/components/ui/tag';
import { getCurrentUser } from '@/lib/api/auth';
import {
  getQuestionnaireInstanceTotalQuestionCount,
  getQuestionnaireResponses,
  sendQuestionnaireReminder,
  type GetQuestionnaireResponsesResponse,
  type QuestionnaireInstanceResponse,
} from '@/lib/api/questionnaires';
import { ClientDetailsV2NotesSection } from './ClientDetailsV2FlagsTab';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { formatDisplayLabel } from '@/utils/display-labels';
import { getMedicalConditionDisplays } from '@/utils/client-medical-conditions';
import { useNavigate } from 'react-router-dom';

interface ClientDetailsV2OverviewTabProps {
  client: Client;
  clientDisplayName: string;
  nextAppointmentDate: string | null;
  goals: GoalItem[];
  conditionNames: string[];
  biomarkerItems: Biomarker[];
  refreshKey?: number;
  onAddAppointment?: () => void;
  onOpenSuggestedRda?: () => void;
  onOpenClientTargets?: () => void;
  onOpenLabReports?: () => void;
  onAssignQuestionnaire?: () => void;
  onOpenNotes?: () => void;
}

const sectionCardSx = {
  borderRadius: { xs: '14px', sm: '16px' },
  border: '1px solid #dbe6eb',
  p: { xs: 1.5, sm: 2 },
  bgcolor: '#fff',
  minWidth: 0,
  alignSelf: 'start',
  display: 'flex',
  flexDirection: 'column',
} as const;

const OverviewCardTabLink: React.FC<{
  label: string;
  onClick?: () => void;
}> = ({ label, onClick }) => (
  <Button
    type="button"
    variant="text"
    size="small"
    onClick={onClick}
    endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
    sx={{
      mt: 'auto',
      alignSelf: 'flex-start',
      minHeight: 30,
      px: 0,
      pt: 1,
      pb: 0,
      borderRadius: 0,
      color: '#2CA7D6',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '0.8125rem',
      lineHeight: 1.2,
      textTransform: 'none',
      '&:hover': {
        bgcolor: 'transparent',
        color: '#148dbc',
        textDecoration: 'underline',
      },
      '& .MuiButton-endIcon': {
        ml: 0.25,
      },
    }}
  >
    {label}
  </Button>
);

function hasValidAppointmentDate(date: string | null | undefined): boolean {
  if (!date) return false;
  const parsed = new Date(date);
  return !Number.isNaN(parsed.getTime());
}

const NextAppointmentCard: React.FC<{
  nextAppointment: ClientOverviewAppointment | null;
  nextAppointmentDate: string | null;
  clientDisplayName: string;
  onRescheduleClick: (appointmentId?: string) => void;
  onAddAppointment?: () => void;
}> = ({ nextAppointment, nextAppointmentDate, clientDisplayName, onRescheduleClick, onAddAppointment }) => {
  const resolvedDate = nextAppointment?.startTime || nextAppointmentDate;
  const hasNextAppointment =
    Boolean(nextAppointment?.startTime) || hasValidAppointmentDate(nextAppointmentDate);
  const hideReschedule =
    !nextAppointment ||
    shouldHideAppointmentJoinRescheduleCancel(
      nextAppointment.status,
      nextAppointment.endTime ?? null,
      nextAppointment.startTime
        ? {
            startAt: new Date(nextAppointment.startTime),
          }
        : undefined,
    );

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionCardSx,
        alignSelf: 'stretch',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <NextAppointmentIcon />
        <Typography sx={panelTitleSx}>Next Appointment</Typography>
      </Box>
      {!hasNextAppointment ? (
        <Box
          sx={{
            mt: 1.5,
            flex: 1,
            minHeight: 148,
            p: 2,
            borderRadius: '12px',
            border: '1px dashed #d5e3eb',
            bgcolor: '#f8fbfd',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: '#e8f6fc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EventOutlinedIcon sx={{ fontSize: 22, color: '#2CA7D6' }} />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              color: '#253047',
              lineHeight: 1.3,
            }}
          >
            No upcoming appointments
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8125rem',
              color: '#667085',
              lineHeight: 1.45,
              maxWidth: 260,
            }}
          >
            Schedule a session to keep {clientDisplayName} on track.
          </Typography>
        </Box>
      ) : (
      <Box
        sx={{
          mt: 1.5,
          p: { xs: 1.5, sm: 1.75 },
          pl: { xs: 1.25, sm: 1.5 },
          borderRadius: '12px',
          border: '1px solid #edf1f5',
          borderLeft: '4px solid #7fd0ef',
          bgcolor: '#fff',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr)' },
          gap: { xs: 1.25, sm: 1.5 },
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            minWidth: { xs: '100%', sm: 72 },
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'column' },
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            alignSelf: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2.35rem',
              lineHeight: 0.92,
              letterSpacing: '-0.04em',
              color: '#2CA7D6',
            }}
          >
            {formatDate(resolvedDate, 'dd', '--')}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: 1,
              letterSpacing: '0.08em',
              color: '#2CA7D6',
            }}
          >
            {formatDate(resolvedDate, 'MMM', '--').toUpperCase()}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, pt: 0.25 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.0625rem',
              lineHeight: 1.08,
              color: '#253047',
              overflowWrap: 'anywhere',
            }}
          >
            {nextAppointment?.title || 'Nutrition Consultation'}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '0.8125rem',
              lineHeight: 1.35,
              color: '#667085',
              overflowWrap: 'anywhere',
            }}
          >
            {formatDate(resolvedDate, 'hh:mm a', '--:--')} • {nextAppointment?.appointmentType === 'offline' ? 'In-Person' : 'Video Call'} • {clientDisplayName}
          </Typography>
          <Box
            sx={{
              mt: 1.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 0.75,
              rowGap: 0.75,
            }}
          >
            {/* Show Join button only if appointment is today */}
            {nextAppointment?.meetingLink && canJoinAppointment(nextAppointment.status, nextAppointment.startTime, nextAppointment.endTime) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<VideocamOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => window.open(nextAppointment.meetingLink, '_blank', 'noopener,noreferrer')}
                sx={{
                  minHeight: 28,
                  minWidth: 88,
                  py: 0.5,
                  borderRadius: '6px',
                  bgcolor: '#2FA8D5',
                  color: '#fff',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  px: 1.5,
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
                  '& .MuiButton-startIcon': { marginRight: 0.5 },
                }}
              >
                Join
              </Button>
            )}
            {!hideReschedule ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onRescheduleClick(nextAppointment?.id)}
                sx={{
                  minHeight: 28,
                  minWidth: 116,
                  py: 0.5,
                  borderRadius: '6px',
                  borderColor: '#d0d5dd',
                  color: '#475467',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  px: 1.5,
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: '#c5ccd6',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                Reschedule
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
      )}
      <OverviewCardTabLink label="Add appointment" onClick={onAddAppointment} />
    </Paper>
  );
};

const QuestionnairesCard: React.FC<{
  progressLabel: string;
  completedCount: number;
  questionnaires: OverviewQuestionnaireCardItem[];
  clientId: string;
  clientDisplayName: string;
  onAssignQuestionnaire?: () => void;
}> = ({ progressLabel, completedCount, questionnaires, clientId, clientDisplayName, onAssignQuestionnaire }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remindingInstanceId, setRemindingInstanceId] = useState<string | null>(null);

  const handleSendReminder = React.useCallback(
    async (instanceId: string, questionnaireName: string) => {
      if (!instanceId || remindingInstanceId) return;

      setRemindingInstanceId(instanceId);
      try {
        await sendQuestionnaireReminder(instanceId);
        showSuccessToast('Reminder email sent', `Reminder sent for ${questionnaireName}.`);
      } catch (error) {
        console.error('Failed to send questionnaire reminder', error);
        showErrorToast(error, extractErrorMessage(error) || 'Could not send reminder. Please try again.');
      } finally {
        setRemindingInstanceId(null);
      }
    },
    [remindingInstanceId]
  );

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : Math.max(0, questionnaires.length - 1)));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < questionnaires.length - 1 ? prev + 1 : 0));
  };

  const latestQuestionnaire = questionnaires[currentIndex];
  const isQuestionnaireEmpty = !latestQuestionnaire;
  const isCompleted = latestQuestionnaire?.status === 'completed';
  const isInProgress = latestQuestionnaire?.status === 'in_progress';
  const canViewResponses = Boolean(
    isCompleted && latestQuestionnaire?.questionnaireId && latestQuestionnaire.instanceId
  );
  const dotColor = isCompleted ? '#10B981' : isInProgress ? '#F59E0B' : '#344054';
  const statusLabel = isCompleted ? 'Completed' : isInProgress ? 'In Progress' : 'Pending';

  const handleViewResponses = React.useCallback(() => {
    if (!latestQuestionnaire?.questionnaireId || !latestQuestionnaire.instanceId) return;

    const query = new URLSearchParams({ clientId });
    if (clientDisplayName.trim()) {
      query.set('clientName', clientDisplayName.trim());
    }

    navigate(
      `/questionnaires/${encodeURIComponent(latestQuestionnaire.questionnaireId)}/responses/${encodeURIComponent(latestQuestionnaire.instanceId)}?${query.toString()}`
    );
  }, [clientDisplayName, clientId, latestQuestionnaire, navigate]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionCardSx,
        alignSelf: 'stretch',
        height: '100%',
        minHeight: isQuestionnaireEmpty ? 178 : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <QuestionnaireIcon />
          <Typography sx={panelTitleSx}>Questionnaire</Typography>
        </Box>
        {questionnaires.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={handlePrevious} sx={{ p: 0.25, color: '#2CA7D6' }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleNext} sx={{ p: 0.25, color: '#2CA7D6' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
        <QuestionnaireProgressIcon />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.8rem' }}>
          {progressLabel}
        </Typography>
      </Box>
      {isQuestionnaireEmpty ? (
        <Box
          sx={{
            mt: 1.5,
            flex: 1,
            minHeight: 148,
            p: 2,
            borderRadius: '12px',
            border: '1px dashed #d5e3eb',
            bgcolor: '#f8fbfd',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: '#e8f6fc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <QuestionnaireIcon />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              color: '#253047',
              lineHeight: 1.3,
            }}
          >
            No Questionnaires
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8125rem',
              color: '#667085',
              lineHeight: 1.45,
              maxWidth: 260,
            }}
          >
            Assign a questionnaire to get started.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mt: 3.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: '#0f172a',
                overflowWrap: 'anywhere',
              }}
            >
              {latestQuestionnaire?.name}
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: dotColor,
                  lineHeight: 1.2,
                }}
              >
                {statusLabel}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.25,
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.8rem' }}>
              {`${latestQuestionnaire?.numberOfQuestions || 0} questions • Assigned ${formatDate(latestQuestionnaire?.assignedAt, 'MMM d, yyyy', 'Not assigned')}`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
            {!isCompleted && (
              <Button
                type="button"
                variant="outlined"
                disabled={
                  !latestQuestionnaire?.instanceId || remindingInstanceId === latestQuestionnaire.instanceId
                }
                onClick={(event) => {
                  event.stopPropagation();
                  if (!latestQuestionnaire?.instanceId) return;
                  void handleSendReminder(
                    latestQuestionnaire.instanceId,
                    latestQuestionnaire.name || 'Questionnaire'
                  );
                }}
                sx={{
                  minHeight: 32,
                  borderRadius: '7px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.375,
                  flexShrink: 0,
                  '&.MuiButton-outlinedPrimary': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                  },
                  '&.MuiButton-outlinedPrimary:hover': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                    bgcolor: '#f8fafc',
                  },
                  '&.MuiButton-outlinedPrimary:active': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                    bgcolor: '#f1f5f9',
                  },
                  '&.MuiButton-outlinedPrimary.Mui-disabled': {
                    borderColor: '#e5e7eb',
                    color: '#98a2b3',
                    bgcolor: 'transparent',
                  },
                }}
              >
                {remindingInstanceId === latestQuestionnaire?.instanceId ? 'Sending…' : 'Send Reminder'}
              </Button>
            )}
            {canViewResponses && (
              <Button
                type="button"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewResponses();
                }}
                sx={{
                  minHeight: 32,
                  borderRadius: '7px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.375,
                  flexShrink: 0,
                  '&.MuiButton-outlinedPrimary': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                  },
                  '&.MuiButton-outlinedPrimary:hover': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                    bgcolor: '#f8fafc',
                  },
                  '&.MuiButton-outlinedPrimary:active': {
                    borderColor: '#d0d5dd',
                    color: '#344054',
                    bgcolor: '#f1f5f9',
                  },
                }}
              >
                View Responses
              </Button>
            )}
            </Box>
          </Box>
        </>
      )}
      <OverviewCardTabLink label="Assign questionnaire" onClick={onAssignQuestionnaire} />
    </Paper>
  );
};

const HealthGoalsCard: React.FC<{
  goals: GoalItem[];
  onOpenSuggestedRda?: () => void;
  onOpenClientTargets?: () => void;
}> = ({
  goals,
  onOpenSuggestedRda,
  onOpenClientTargets,
}) => {
  const hasScrollableGoals = goals.length > 3;

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionCardSx,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <HealthGoalsIcon />
        <Typography sx={panelTitleSx}>Health Goals</Typography>
      </Box>
      <Box
        sx={{
          mt: 1.75,
          display: 'grid',
          gap: 1.75,
          maxHeight: hasScrollableGoals ? 176 : undefined,
          overflowY: hasScrollableGoals ? 'auto' : undefined,
          pr: hasScrollableGoals ? 2.5 : undefined,
          scrollbarGutter: hasScrollableGoals ? 'stable both-edges' : undefined,
          scrollbarWidth: hasScrollableGoals ? 'thin' : undefined,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '999px',
            bgcolor: '#98a2b3',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
        }}
      >
        {goals.map((goal, index) => (
          // Use explicit value if provided, or assume 0 until calculated
          <Box key={index} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    color: '#0f172a',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {goal.label}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085' }}>
                  {goal.subtitle}
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#667085', flexShrink: 0 }}>
                {goal.progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={goal.progress}
              sx={{
                mt: 0.7,
                height: 6,
                borderRadius: 999,
                bgcolor: '#e4e7ec',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: '#25a8da',
                },
              }}
            />
          </Box>
        ))}
      </Box>
      <OverviewCardTabLink label="View suggested RDA" onClick={onOpenSuggestedRda} />
      <OverviewCardTabLink label="View client targets" onClick={onOpenClientTargets} />
    </Paper>
  );
};

export interface MedicalConditionDisplay {
  name: string;
  autoimmuneSummary?: string;
  status?: string;
}

function getMedicalConditionChipSx(status?: string) {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (normalized === 'active') {
    return {
      bgcolor: '#fff5f6',
      color: '#d92d20',
      border: '1px solid #fecdd3',
    };
  }
  return {
    bgcolor: '#f2f4f7',
    color: '#64748b',
    border: '1px solid #e4e7ec',
  };
}

type OverviewQuestionnaireCardItem = {
  instanceId: string;
  questionnaireId?: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | string;
  numberOfQuestions?: number;
  assignedAt?: string;
};

function resolveQuestionnaireInstanceId(questionnaire: QuestionnaireInstanceResponse): string {
  const raw = questionnaire as QuestionnaireInstanceResponse & {
    _id?: string;
    id?: string;
    shareId?: string;
  };
  const candidate = raw.instanceId ?? raw.shareId ?? raw._id ?? raw.id;
  return typeof candidate === 'string' ? candidate.trim() : '';
}

function mapQuestionnaireInstanceToOverviewCardItem(
  questionnaire: QuestionnaireInstanceResponse
): OverviewQuestionnaireCardItem {
  return {
    instanceId: resolveQuestionnaireInstanceId(questionnaire),
    questionnaireId: questionnaire.questionnaireId,
    name: questionnaire.questionnaireTitle || questionnaire.questionnaireName || 'Questionnaire',
    status: questionnaire.status,
    numberOfQuestions: getQuestionnaireInstanceTotalQuestionCount(questionnaire),
    assignedAt: questionnaire.createdAt,
  };
}

/** Lower rank = earlier in list: high/critical, low/borderline, normal, other. */
function biomarkerStatusSortRank(status?: string): number {
  const s = String(status ?? '').toLowerCase();
  if (s === 'high' || s === 'critical') return 0;
  if (s === 'low' || s === 'borderline') return 1;
  if (s === 'normal') return 2;
  return 3;
}

const MedicalConditionsCard: React.FC<{
  conditions: MedicalConditionDisplay[];
  biomarkerItems: Biomarker[];
  onOpenLabReports?: () => void;
}> = ({
  conditions,
  biomarkerItems,
  onOpenLabReports,
}) => {
  const [labResultsOpen, setLabResultsOpen] = useState(true);

  const sortedBiomarkerItems = useMemo(
    () =>
      [...biomarkerItems].sort((a, b) => {
        const byStatus = biomarkerStatusSortRank(a.status) - biomarkerStatusSortRank(b.status);
        if (byStatus !== 0) return byStatus;
        const nameA = String(a.biomarkerName ?? a.biomarkerId ?? '');
        const nameB = String(b.biomarkerName ?? b.biomarkerId ?? '');
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      }),
    [biomarkerItems],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionCardSx,
        alignSelf: 'stretch',
        height: '100%',
      }}
    >
      <Typography sx={panelTitleSx}>Medical Conditions</Typography>
      <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {conditions.length ? (
          conditions.map((condition, index) => {
            const chipTone = getMedicalConditionChipSx(condition.status);
            return (
            <Tag
              key={`${condition.name}-${index}`}
              variant="neutral"
              size="md"
              truncate={false}
              maxWidth="100%"
              className="items-start px-3 py-1.5 font-[Inter,sans-serif] text-[0.8rem]"
              sx={{
                height: 'auto',
                minHeight: 28,
                bgcolor: chipTone.bgcolor,
                color: chipTone.color,
                border: chipTone.border,
              }}
            >
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 'inherit', color: 'inherit', lineHeight: 1.2 }}>
                    {condition.name}
                  </Typography>
                  {condition.autoimmuneSummary && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.8, mt: 0.2, lineHeight: 1.2 }}>
                      {condition.autoimmuneSummary}
                    </Typography>
                  )}
                </Box>
            </Tag>
            );
          })
        ) : (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.8rem' }}>
            No medical conditions recorded yet.
          </Typography>
        )}
      </Box>

      {sortedBiomarkerItems.length > 0 ? (
        <>
          <Box
            component="button"
            type="button"
            onClick={() => setLabResultsOpen((open) => !open)}
            aria-expanded={labResultsOpen}
            sx={{
              mt: 1.5,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 1,
              px: 0.5,
              border: 'none',
              borderTop: '1px solid #edf1f5',
              bgcolor: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              borderRadius: 0,
              '&:focus-visible': { outline: '2px solid #2CA7D6', outlineOffset: 2 },
            }}
          >
            <Typography sx={{ ...panelTitleSx, mb: 0 }}>Lab results</Typography>
            <ExpandMoreIcon
              sx={{
                color: '#475467',
                transition: 'transform 0.2s ease',
                transform: labResultsOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>
          <Collapse in={labResultsOpen}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.25,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
                pt: 0.25,
              }}
            >
              {sortedBiomarkerItems.map((biomarker) => {
                const color = getBiomarkerColor(biomarker.status);
                const sourceLabel = String(biomarker.questionnaireInstanceId ?? '').trim()
                  ? 'From questionnaire'
                  : 'From biomarkers';
                return (
                  <Box
                    key={biomarker.id}
                    sx={{
                      borderRadius: '12px',
                      p: 1.5,
                      border: `1px solid ${color.border}`,
                      bgcolor: color.bg,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        color: '#0f172a',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {biomarker.biomarkerName || toTitleCase(biomarker.biomarkerId)}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.6,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        color: color.text,
                      }}
                    >
                      {biomarker.value} {biomarker.unit}
                    </Typography>
                    <Typography sx={{ mt: 0.6, fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.75rem' }}>
                      Target status: {toTitleCase(biomarker.status || 'unknown')}
                    </Typography>
                    <Typography sx={{ mt: 0.4, fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.75rem' }}>
                      {sourceLabel}
                    </Typography>
                    <Typography sx={{ mt: 0.4, fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.75rem' }}>
                      Added at: {formatDate(biomarker.addedAt, 'MMM d, yyyy', 'Not available')}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </>
      ) : (
        <Typography sx={{ mt: 1.5, fontFamily: 'Inter, sans-serif', color: '#667085', fontSize: '0.8rem' }}>
          No biomarker values available yet.
        </Typography>
      )}
      <OverviewCardTabLink label="View lab reports" onClick={onOpenLabReports} />
    </Paper>
  );
};

export const ClientDetailsV2OverviewTab: React.FC<ClientDetailsV2OverviewTabProps & { onRescheduleAppointment?: (appointmentId?: string) => void }> = ({
  client,
  clientDisplayName,
  nextAppointmentDate,
  goals,
  conditionNames,
  biomarkerItems,
  refreshKey,
  onAddAppointment,
  onOpenSuggestedRda,
  onOpenClientTargets,
  onOpenLabReports,
  onAssignQuestionnaire,
  onOpenNotes,
  onRescheduleAppointment,
}) => {
  const [overviewData, setOverviewData] = useState<ClientOverviewResponse | null>(null);
  const [questionnaireResponsesData, setQuestionnaireResponsesData] = useState<GetQuestionnaireResponsesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const nutritionistId = React.useMemo(() => getCurrentUser()?.id ?? undefined, []);

  useEffect(() => {
    const fetchOverview = async () => {
      const [overviewResult, questionnaireResponsesResult] = await Promise.allSettled([
        getClientOverview(client.id),
        getQuestionnaireResponses(client.id, nutritionistId),
      ]);

      if (overviewResult.status === 'fulfilled') {
        setOverviewData(overviewResult.value);
      } else {
        console.error('Failed to fetch client overview:', overviewResult.reason);
      }

      if (questionnaireResponsesResult.status === 'fulfilled') {
        setQuestionnaireResponsesData(questionnaireResponsesResult.value);
      } else {
        console.error('Failed to fetch questionnaire responses:', questionnaireResponsesResult.reason);
      }

      setIsLoading(false);
    };
    if (client) fetchOverview();
  }, [client, nutritionistId, refreshKey]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Keep questionnaire progress/counts sourced from questionnaire responses only
  // so Overview stays aligned with the Questionnaires section.
  const resolvedNextAppointmentDate = overviewData?.nextAppointment?.startTime || nextAppointmentDate;
  const resolvedCompletedQuestionnaires = questionnaireResponsesData?.completedCount ?? 0;
  const resolvedQuestionnaireItems = (questionnaireResponsesData?.questionnaires ?? [])
    .map(mapQuestionnaireInstanceToOverviewCardItem);
  const resolvedConditions: MedicalConditionDisplay[] = overviewData?.medicalConditions
    ? getMedicalConditionDisplays(overviewData.medicalConditions)
    : Array.isArray(client.medicalConditionDetails) && client.medicalConditionDetails.length > 0
      ? getMedicalConditionDisplays(client.medicalConditionDetails)
      : conditionNames.map((name) => ({ name: formatDisplayLabel(name), status: 'active' }));

  const resolvedQuestionnaireTotal = questionnaireResponsesData?.totalInstances ?? 0;
  const resolvedQuestionnaireProgressLabel = `${resolvedCompletedQuestionnaires}/${resolvedQuestionnaireTotal} Completed`;
  
  // Format goals using the overview goals API when available, otherwise fallback
  let resolvedGoals = goals;
  if (overviewData?.goals) {
    const apiGoals: typeof goals = [];
    if (overviewData.goals.weightGoal) {
      apiGoals.push({ label: 'Weight Goal', subtitle: toTitleCase(overviewData.goals.weightGoal), progress: 0 });
    }
    (overviewData.goals.healthGoals || []).forEach(goal => {
      apiGoals.push({ label: 'Health Goal', subtitle: goal, progress: 0 });
    });

    const clientGoals = overviewData.goals.clientGoals;
    if (clientGoals) {
      if (clientGoals.protein?.grams?.value || clientGoals.protein?.kcal?.value) {
        const parts = [];
        if (clientGoals.protein.grams?.value) parts.push(`${clientGoals.protein.grams.value} ${clientGoals.protein.grams.unit || 'g'}`);
        if (clientGoals.protein.kcal?.value) parts.push(`${clientGoals.protein.kcal.value} ${clientGoals.protein.kcal.unit || 'kcal'}`);
        apiGoals.push({ label: 'Protein', subtitle: parts.join(' • '), progress: 0 });
      }

      if (clientGoals.carbohydrates?.grams?.value || clientGoals.carbohydrates?.energyPercent?.value) {
        const parts = [];
        if (clientGoals.carbohydrates.grams?.value) parts.push(`${clientGoals.carbohydrates.grams.value} ${clientGoals.carbohydrates.grams.unit || 'g'}`);
        if (clientGoals.carbohydrates.energyPercent?.value) parts.push(`${clientGoals.carbohydrates.energyPercent.value} ${clientGoals.carbohydrates.energyPercent.unit || '% E'}`);
        apiGoals.push({ label: 'Carbohydrates', subtitle: parts.join(' • '), progress: 0 });
      }

      if (clientGoals.dietaryFibre?.value) {
        apiGoals.push({ label: 'Dietary Fibre', subtitle: `${clientGoals.dietaryFibre.value} ${clientGoals.dietaryFibre.unit || 'g/day'}`, progress: 0 });
      }

      if (clientGoals.totalFat?.grams?.value || clientGoals.totalFat?.kcal?.value) {
        const parts = [];
        if (clientGoals.totalFat.grams?.value) parts.push(`${clientGoals.totalFat.grams.value} ${clientGoals.totalFat.grams.unit || 'g'}`);
        if (clientGoals.totalFat.kcal?.value) parts.push(`${clientGoals.totalFat.kcal.value} ${clientGoals.totalFat.kcal.unit || 'kcal'}`);
        apiGoals.push({ label: 'Total Fat', subtitle: parts.join(' • '), progress: 0 });
      }

      if (clientGoals.saturatedFat?.grams?.value || clientGoals.saturatedFat?.kcal?.value) {
        const parts = [];
        if (clientGoals.saturatedFat.grams?.value) parts.push(`${clientGoals.saturatedFat.grams.value} ${clientGoals.saturatedFat.grams.unit || 'g'}`);
        if (clientGoals.saturatedFat.kcal?.value) parts.push(`${clientGoals.saturatedFat.kcal.value} ${clientGoals.saturatedFat.kcal.unit || 'kcal'}`);
        apiGoals.push({ label: 'Saturated Fat', subtitle: parts.join(' • '), progress: 0 });
      }

      if (clientGoals.water?.value) {
        apiGoals.push({ label: 'Water', subtitle: `${clientGoals.water.value} ${clientGoals.water.unit || 'L/day'}`, progress: 0 });
      }
    }

    if (apiGoals.length > 0) {
      resolvedGoals = apiGoals;
    } else if (!overviewData.goals.weightGoal && !(overviewData.goals.healthGoals?.length)) {
      resolvedGoals = goals; // fallback if nothing was extracted
    }
  }

  return (
    <Box
      sx={{
        mt: { xs: 1.5, lg: 2.25 },
        display: 'grid',
        gap: { xs: 1.25, lg: 1.5 },
        alignItems: 'start',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1fr)',
        },
      }}
    >
      <NextAppointmentCard
        nextAppointment={overviewData?.nextAppointment ?? null}
        nextAppointmentDate={resolvedNextAppointmentDate}
        clientDisplayName={clientDisplayName}
        onRescheduleClick={(id) => onRescheduleAppointment?.(id)}
        onAddAppointment={onAddAppointment}
      />
      <QuestionnairesCard
        progressLabel={resolvedQuestionnaireProgressLabel}
        completedCount={resolvedCompletedQuestionnaires}
        questionnaires={resolvedQuestionnaireItems}
        clientId={client.id}
        clientDisplayName={clientDisplayName}
        onAssignQuestionnaire={onAssignQuestionnaire}
      />
      <Box
        sx={{
          gridColumn: { xs: 'auto', lg: '3 / 4' },
          gridRow: { xs: 'auto', lg: '1 / span 2' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.25, lg: 1.5 },
          minWidth: 0,
          alignSelf: 'start',
        }}
      >
        <HealthGoalsCard
          goals={resolvedGoals}
          onOpenSuggestedRda={onOpenSuggestedRda}
          onOpenClientTargets={onOpenClientTargets}
        />
        <Paper elevation={0} sx={{ ...sectionCardSx, width: '100%' }}>
          <ClientDetailsV2NotesSection
            clientId={client.id}
            sectionTitle="Notes"
            showTypeSelector={false}
            showFlagLabels={false}
            compact
            sx={{ mt: 0 }}
            listMaxHeight={360}
            previewLimit={3}
            onViewAll={onOpenNotes}
          />
        </Paper>
      </Box>
      <Box sx={{ gridColumn: { xs: 'auto', lg: '1 / span 2' }, minWidth: 0 }}>
        <MedicalConditionsCard
          conditions={resolvedConditions}
          biomarkerItems={biomarkerItems}
          onOpenLabReports={onOpenLabReports}
        />
      </Box>
    </Box>
  );
};
